import React from "react"
import {useTranslation} from "react-i18next"
import {differenceInCalendarDays, parseISO} from "date-fns"
import {twMerge} from "tailwind-merge"

import {Table} from "../../../components/Table/Table.tsx"
import {TableNoRows} from "../../../components/Table/TableNoRows.tsx"
import {TableRow} from "../../../components/Table/TableRow.tsx"
import {EOrderDirection, TColumnsMetaWithEmpty, TypedTableContext} from "../../../components/Table/utils/shared.ts"
import {Tooltip} from "../../../components/Tooltip.tsx"
import {useKlaraDealsQuery} from "../../../queries/klaraDashboard.ts"
import {
  AKlaraDashboardDeal,
  AKlaraDashboardDealSortByValues,
  ASalesAssistantDealSortDirectionValues,
} from "../../../services/types.generated.ts"
import {getFullName} from "../../../utils/index.ts"
import {KlaraFiltersContext, TKlaraLeadsColumn, TKlaraLeadsOrder} from "../filters.tsx"
import {leadName} from "../leadName.ts"
import {getPeriodRange} from "../period.ts"
import {activityTypeLabel, displayStatus, formatActivityDate, issueLines, statusSubline} from "../status.ts"
import {dashboardDay, TDashboardClock, useDashboardClock} from "../useDashboardClock.ts"
import {useKlaraPeriod} from "../useKlaraPeriod.ts"
import {CardState} from "./CardState.tsx"
import {CARD_CLASS_NAME, TOOLTIP_CLASS_NAME} from "./cardStyles.ts"
import {LeadsHeaderCell} from "./LeadsHeaderCell.tsx"
import {LeadsPagination} from "./LeadsPagination.tsx"
import {LeadsTabs} from "./LeadsTabs.tsx"
import {LeadsToolbar} from "./LeadsToolbar.tsx"
import {PriorityMenu} from "./PriorityMenu.tsx"
import {RemindButton} from "./RemindButton.tsx"
import {StatusPill} from "./StatusPill.tsx"

type TLeadsMetadata = {clock: TDashboardClock; descriptionIdPrefix: string}

const issuesDescriptionId = (prefix: string, deal: AKlaraDashboardDeal) => `${prefix}-issues-${deal.id}`

const LeadsTableContext = TypedTableContext<TKlaraLeadsColumn, AKlaraDashboardDeal, TLeadsMetadata>()

const EMPTY_DEALS: AKlaraDashboardDeal[] = []

const SORT_BY: Partial<Record<TKlaraLeadsColumn, AKlaraDashboardDealSortByValues>> = {
  lead: AKlaraDashboardDealSortByValues.Title,
  priority: AKlaraDashboardDealSortByValues.Priority,
  stage: AKlaraDashboardDealSortByValues.StageDays,
  next_activity: AKlaraDashboardDealSortByValues.NextActivity,
}

/** The table's sorted column as the backend's sort params; none keeps the backend's default order. */
const sortParams = (order: TKlaraLeadsOrder) => {
  const sortBy = order && SORT_BY[order.column]
  if (!order || !sortBy) {
    return {}
  }

  return {
    sortBy,
    sortDirection:
      order.direction === EOrderDirection.ASC
        ? ASalesAssistantDealSortDirectionValues.Asc
        : ASalesAssistantDealSortDirectionValues.Desc,
  }
}

/** Whether the Remind action is offered; off removes the Actions column entirely. */
const IS_REMIND_AVAILABLE = true

export const LeadsTable: React.FC<{isRemindAvailable?: boolean}> = ({isRemindAvailable = IS_REMIND_AVAILABLE}) => {
  const {t} = useTranslation()
  const {view, chip, order, setOrder, page, setPage, pageSize, priorities, search, salesPersonExternalId, openDrawer} =
    KlaraFiltersContext.useContext()
  const [period] = useKlaraPeriod()
  // The picker computes preset dates on the browser's calendar.
  const range = React.useMemo(() => getPeriodRange(period), [period])
  const titleId = React.useId()

  const {data, isPending, isError, isSuccess, isPlaceholderData, isFetching, refetch} = useKlaraDealsQuery({
    ...range,
    view,
    chip: chip ?? undefined,
    priorities,
    salesPersonExternalId,
    search,
    ...sortParams(order),
    page,
    pageSize,
  })
  const deals = data?.data.deals ?? EMPTY_DEALS
  // After a failed request the previous response no longer describes the filters, so its counts are not shown.
  const meta = isError && !isFetching ? undefined : data?.data.meta
  // A previous filter's response (placeholder) or one being refreshed keeps its counts on screen, dimmed.
  const areCountsStale = !isSuccess || isPlaceholderData || isFetching
  const total = meta?.pagination.total_count

  // When the leads shrink under the open page (e.g. after a refetch), move to the last page that still exists.
  // Only a settled response for this very page counts, never the previous page's placeholder.
  const responseTotal = data?.data.meta.pagination.total_count
  const lastPage = responseTotal == null ? null : Math.max(1, Math.ceil(responseTotal / pageSize))
  React.useEffect(() => {
    if (isSuccess && !isPlaceholderData && lastPage != null && page > lastPage) {
      setPage(lastPage)
    }
  }, [isSuccess, isPlaceholderData, lastPage, page, setPage])

  // Read once for the whole table; the cells take it from the table metadata.
  const {timeZone, asOf} = useDashboardClock()
  const asOfTime = asOf.getTime()
  const descriptionIdPrefix = React.useId()
  const metadata = React.useMemo<TLeadsMetadata>(
    () => ({clock: {timeZone, asOf: new Date(asOfTime)}, descriptionIdPrefix}),
    [timeZone, asOfTime, descriptionIdPrefix]
  )

  const openLead = React.useCallback(
    (row: AKlaraDashboardDeal, event: React.MouseEvent) => {
      // The drawer returns focus to what was focused when it opened, so a click anywhere on the row lands on its lead.
      event.currentTarget.querySelector<HTMLElement>("[data-lead-opener]")?.focus()
      openDrawer({kind: "lead", dealId: row.id})
    },
    [openDrawer]
  )

  const columnsMeta = React.useMemo<TColumnsMetaWithEmpty<TKlaraLeadsColumn, AKlaraDashboardDeal>>(
    () => [
      {
        column: "lead",
        size: "minmax(160px, 1.8fr)",
        sortFn: true,
        HeaderCell: LeadsHeaderCell,
        cellClassName: CELL_CLASS_NAME,
        HeaderCellValue: () => t("KlaraDashboard_Leads_Column_Lead"),
        CellValue: LeadCell,
      },
      {
        column: "sales_person",
        size: "minmax(110px, 1.2fr)",
        HeaderCell: LeadsHeaderCell,
        cellClassName: CELL_CLASS_NAME,
        HeaderCellValue: () => t("KlaraDashboard_Leads_Column_SalesPerson"),
        CellValue: ({row}) => (
          <span className={"min-w-0 truncate text-cr-grey-80"}>{getFullName(row.sales_person, "—")}</span>
        ),
      },
      {
        column: "priority",
        size: "minmax(100px, 1fr)",
        sortFn: true,
        HeaderCell: LeadsHeaderCell,
        cellClassName: CELL_CLASS_NAME,
        HeaderCellValue: () => t("KlaraDashboard_Leads_Column_Priority"),
        CellValue: PriorityMenu,
      },
      {
        column: "stage",
        size: "minmax(130px, 1.6fr)",
        sortFn: true,
        HeaderCell: LeadsHeaderCell,
        cellClassName: CELL_CLASS_NAME,
        HeaderCellValue: () => t("KlaraDashboard_Leads_Column_Stage"),
        CellValue: StageCell,
      },
      {
        column: "status",
        size: "minmax(140px, 1.6fr)",
        HeaderCell: LeadsHeaderCell,
        cellClassName: CELL_CLASS_NAME,
        HeaderCellValue: () => t("KlaraDashboard_Leads_Column_Status"),
        CellValue: StatusCell,
      },
      {
        column: "next_activity",
        size: "minmax(130px, 1.3fr)",
        sortFn: true,
        HeaderCell: LeadsHeaderCell,
        cellClassName: CELL_CLASS_NAME,
        HeaderCellValue: () => t("KlaraDashboard_Leads_Column_NextActivity"),
        CellValue: NextActivityCell,
      },
      isRemindAvailable && {
        column: "actions",
        size: "max-content",
        align: "right",
        HeaderCell: LeadsHeaderCell,
        cellClassName: twMerge(CELL_CLASS_NAME, "pl-0 md:pl-0"),
        HeaderCellValue: () => <span className={"sr-only"}>{t("KlaraDashboard_Leads_Column_Actions")}</span>,
        CellValue: ({row}) => <RemindButton dealId={row.id} />,
      },
    ],
    [t, isRemindAvailable]
  )

  return (
    <section aria-labelledby={titleId} className={twMerge(CARD_CLASS_NAME, "flex flex-col px-6 py-5")}>
      <LeadsToolbar
        titleId={titleId}
        total={total}
        isTotalStale={areCountsStale}
        counts={areCountsStale ? undefined : meta?.counts}
      />
      <LeadsTabs counts={meta?.counts.views} isStale={areCountsStale} />
      <CardState query={{isPending, isError, isFetching, refetch}} skeletonClassName={"mt-3 h-96"}>
        <Table<TKlaraLeadsColumn, AKlaraDashboardDeal, TLeadsMetadata>
          // The table grows with its rows; only a narrow screen scrolls it sideways.
          className={"mt-3 max-h-none rounded-none"}
          data={deals}
          columnsMeta={columnsMeta}
          metadata={metadata}
          orderBy={order}
          onOrder={setOrder}
          ghost
        >
          {({data, pinnedColumn}) => (
            <>
              {data.map((row, index) => (
                <TableRow
                  key={row.id}
                  testId={`row-${row.id}`}
                  row={row}
                  rowIndex={index}
                  className={"whitespace-pre-wrap"}
                  pinnedColumn={pinnedColumn}
                  onClick={openLead}
                />
              ))}
              {data.length === 0 && <TableNoRows>{t("KlaraDashboard_Leads_Empty")}</TableNoRows>}
            </>
          )}
        </Table>
        {/* Each lead's issues, outside the cells so they stay out of the row's text. */}
        <div className={"sr-only"}>
          {deals.map(deal => {
            const issues = issueLines(deal, metadata.clock)
            return (
              issues.length > 0 && (
                <span key={deal.id} id={issuesDescriptionId(descriptionIdPrefix, deal)}>
                  {issues.join(", ")}
                </span>
              )
            )
          })}
        </div>
      </CardState>
      <LeadsPagination page={page} setPage={setPage} pageSize={pageSize} total={responseTotal} />
    </section>
  )
}

const CELL_CLASS_NAME =
  "px-2.5 py-3 text-[12.5px] md:px-2.5 md:py-3 border-b border-b-cr-slate-6 group-first-of-type/row:border-t-0 group-last/row:border-b group-hover/row:bg-cr-slate-1"

const SUBLINE_CLASS_NAME = "mt-0.5 truncate text-[11.5px] text-cr-slate-60"

const LeadCell: React.FC<{row: AKlaraDashboardDeal}> = ({row}) => {
  const {openDrawer} = KlaraFiltersContext.useContext()
  const {clock, descriptionIdPrefix} = LeadsTableContext.useContext().metadata
  const name = leadName(row)
  const organisation = row.organization?.name?.trim()
  const issues = issueLines(row, clock)

  return (
    <div className={"flex min-w-0 flex-col"}>
      <Tooltip
        disabled={issues.length === 0}
        openOnFocus
        closeOnEscape
        buttonNode={
          // The row's keyboard entry point: Enter on it opens the lead like a click anywhere on the row.
          <button
            type={"button"}
            data-lead-opener
            aria-describedby={issues.length > 0 ? issuesDescriptionId(descriptionIdPrefix, row) : undefined}
            className={"max-w-full truncate text-left font-semibold text-cr-slate-90 hover:underline"}
            onClick={() => openDrawer({kind: "lead", dealId: row.id})}
          >
            <span data-testid={"lead name"}>{name}</span>
          </button>
        }
      >
        <div role={"tooltip"} className={TOOLTIP_CLASS_NAME}>
          {issues.map(issue => (
            <div key={issue}>{issue}</div>
          ))}
        </div>
      </Tooltip>
      {organisation && organisation !== name && <span className={SUBLINE_CLASS_NAME}>{organisation}</span>}
    </div>
  )
}

const StageCell: React.FC<{row: AKlaraDashboardDeal}> = ({row}) => {
  const {t} = useTranslation()

  if (!row.stage) {
    return "—"
  }

  return (
    <div className={"flex min-w-0 flex-col"}>
      <span className={"truncate text-cr-slate-90"}>{row.stage.name}</span>
      {row.stage_days != null && (
        <span className={twMerge(SUBLINE_CLASS_NAME, row.flags.rotten && "text-cr-yellow-deep")}>
          {t("KlaraDashboard_Leads_StageDays", {count: row.stage_days})}
        </span>
      )}
    </div>
  )
}

const StatusCell: React.FC<{row: AKlaraDashboardDeal}> = ({row}) => {
  const {t} = useTranslation()
  const {clock} = LeadsTableContext.useContext().metadata
  const status = displayStatus(row)
  const moreIssues = issueLines(row, clock).length - 1
  const subline = moreIssues > 0 ? t("KlaraDashboard_Leads_MoreIssues", {count: moreIssues}) : statusSubline(row, clock)

  return (
    <div className={"flex min-w-0 flex-col"}>
      <StatusPill status={status} />
      {subline && <span className={SUBLINE_CLASS_NAME}>{subline}</span>}
    </div>
  )
}

const NextActivityCell: React.FC<{row: AKlaraDashboardDeal}> = ({row}) => {
  const {t} = useTranslation()
  const {clock} = LeadsTableContext.useContext().metadata
  // The oldest overdue activity comes first; otherwise the earliest planned one.
  const activity = row.overdue_activity ?? row.next_activity

  if (!activity) {
    return <span className={"font-semibold text-cr-slate-40"}>{t("KlaraDashboard_Leads_NoNextActivity")}</span>
  }

  return (
    <div className={"flex min-w-0 flex-col"}>
      <span className={"truncate font-semibold text-cr-slate-90"}>
        <span>{activityTypeLabel(activity.type)}</span> · <span>{formatActivityDate(activity.date, clock)}</span>
      </span>
      {row.overdue_activity ? (
        <span className={twMerge(SUBLINE_CLASS_NAME, "text-cr-red-text")}>
          {t("KlaraDashboard_Leads_DaysOverdue", {
            count: Math.max(1, differenceInCalendarDays(parseISO(dashboardDay(clock)), parseISO(activity.date))),
          })}
        </span>
      ) : (
        <span className={SUBLINE_CLASS_NAME}>{t("KlaraDashboard_Leads_Status_Planned")}</span>
      )}
    </div>
  )
}
